/**
 * The "What do you want?" cards (spec §3, §4.2, §12.5): Cut & fill volumes
 * and Deviation map, ticked independently.
 *
 * Each card is a real checkbox with the WHOLE card as its click target,
 * named (`aria-labelledby`) by its title — plus the PRO badge when locked —
 * and described (`aria-describedby`) by its description and, when there is
 * one, the reason it cannot be used. The selected state is a check glyph plus a heavier
 * border, never colour alone. Two "cannot use" states, as `AddOnToggle` has:
 *
 *  - **unavailable** (the inputs rule it out): the HTML `disabled` attribute,
 *    with the reason in text inside the card — never a hidden option;
 *  - **locked** (subscription gate closed): a text PRO badge and
 *    `aria-disabled`, but still clickable, because the click is what opens
 *    the upgrade dialog (CLAUDE.md feature gating). A locked card never reads
 *    as ticked. Locked wins over unavailable so the upsell stays reachable.
 */
import { CheckIcon } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import type { FeatureResult } from '@/hooks/useFeature';
import { COMPARE_RESULTS, type CompareResult, type ResultAvailability } from '@/lib/analyses/compare/availability';
import { translateKey } from '@/lib/analyses/translateKey';
import { cn } from '@/lib/utils';

const CARD_KEYS = {
  cutFill: { title: 'compareSurfaces.results.cutFill.title', description: 'compareSurfaces.results.cutFill.description' },
  deviation: {
    title: 'compareSurfaces.results.deviation.title',
    description: 'compareSurfaces.results.deviation.description',
  },
} as const satisfies Record<CompareResult, { title: string; description: string }>;

export type ResultLock = Pick<FeatureResult, 'gate' | 'promptUpgrade'>;

export interface ResultCardsProps {
  selected: readonly CompareResult[];
  availability: Record<CompareResult, ResultAvailability>;
  /** A closed gate per result, or null when the result is open. */
  locks: Record<CompareResult, ResultLock | null>;
  onToggle: (result: CompareResult, checked: boolean) => void;
  /** A polite status line under the cards (e.g. "… — unticked"). */
  note?: ReactNode;
  /** Show "Choose at least one result" while nothing is ticked. The dialog
   *  sets it only after a submit attempt or when the USER unticked the last
   *  card — an automatic untick explains itself in `note` instead. */
  showEmptyAlert: boolean;
}

export function ResultCards({ selected, availability, locks, onToggle, note, showEmptyAlert }: ResultCardsProps) {
  const { t } = useTranslation('project');
  const id = useId();
  const visible = COMPARE_RESULTS.filter((r) => availability[r].visible);
  const ticked = visible.filter((r) => selected.includes(r) && !locks[r]);

  return (
    <div className='flex min-w-0 flex-col gap-2'>
      {/* Two columns only when the DIALOG (not the viewport) has room: a long
          German or Hungarian title in a narrow dialog stays one per row. */}
      <div className='@container min-w-0'>
        <div className='grid min-w-0 grid-cols-1 gap-3 @min-[420px]:grid-cols-2'>
          {visible.map((r) => {
            const lock = locks[r];
            const locked = lock !== null;
            const unavailable = !availability[r].available;
            const on = ticked.includes(r);
            const reasonKey = availability[r].reasonKey;
            const titleId = `${id}-${r}-title`;
            const badgeId = `${id}-${r}-badge`;
            const descId = `${id}-${r}-desc`;
            const reasonId = `${id}-${r}-reason`;
            return (
              <label
                key={r}
                data-testid={`result-${r}`}
                data-compare-card={r}
                data-state={on ? 'checked' : 'unchecked'}
                className={cn(
                  // `relative`: the sr-only checkbox is absolutely positioned;
                  // without a positioned card its containing block is the
                  // dialog, and focusing it scrolled the dialog itself.
                  'relative flex min-w-0 flex-col gap-1 rounded-md transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background',
                  on
                    ? 'border-2 border-primary bg-primary/5 p-[11px]'
                    : 'border border-border p-3',
                  unavailable && !locked ? 'cursor-not-allowed' : 'cursor-pointer',
                )}>
                <input
                  type='checkbox'
                  className='sr-only'
                  checked={on}
                  disabled={unavailable && !locked}
                  aria-disabled={locked ? true : undefined}
                  data-locked={locked ? 'true' : undefined}
                  // Named by the title (+ the PRO badge), described by the rest:
                  // without it the whole card label would be the name and the
                  // description would be read twice.
                  aria-labelledby={locked ? `${titleId} ${badgeId}` : titleId}
                  aria-describedby={unavailable && reasonKey ? `${descId} ${reasonId}` : descId}
                  onChange={(e) => {
                    if (lock) {
                      lock.promptUpgrade();
                      return;
                    }
                    onToggle(r, e.target.checked);
                  }}
                />
                <span className={cn('flex min-w-0 items-start gap-2', (locked || unavailable) && 'opacity-60')}>
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                    )}>
                    {on && <CheckIcon data-testid={`result-${r}-check`} className='size-3.5' />}
                  </span>
                  <span id={titleId} className='min-w-0 flex-1 text-sm font-medium break-words'>
                    {t(CARD_KEYS[r].title)}
                  </span>
                  {lock && (
                    <span id={badgeId} data-testid={`result-${r}-upgrade`}>
                      <UpgradeBadge gate={lock.gate} />
                    </span>
                  )}
                </span>
                <span id={descId} className='pl-6 text-xs break-words text-muted-foreground'>
                  {t(CARD_KEYS[r].description)}
                </span>
                {unavailable && reasonKey && (
                  <span
                    id={reasonId}
                    data-testid={`result-${r}-reason`}
                    className='pl-6 text-xs break-words text-muted-foreground italic'>
                    {translateKey(t, reasonKey)}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>
      {ticked.length === 0 && showEmptyAlert && (
        <p role='alert' data-testid='compare-no-result' className='text-sm text-destructive'>
          {t('compareSurfaces.results.noResult')}
        </p>
      )}
      {/* Always mounted and never display:none: a live region announces
          changes, not insertions, and one taken out of the accessibility tree
          announces nothing. Empty, it only cancels the column gap. */}
      <p
        role='status'
        data-testid='compare-results-note'
        className='text-xs text-amber-700 empty:-mt-2 dark:text-amber-400'>
        {note}
      </p>
    </div>
  );
}
